import {ProductSurface} from './types'

export type Product = 'live-easy' | 'web'

/**
 * Handles all of our analytics needs.
 */
class Analytics {
  /**
   * Checks whether analytics is loaded.
   */
  get isDefined() {
    /* istanbul ignore next */
    if (typeof window === 'undefined') {
      return false
    }
    return 'analytics' in window
  }

  /**
   * Calls Segment's .page method to track page views.
   * @returns nothing
   */
  page() {
    if (!this.isDefined) return
    window.analytics.page()
  }

  /**
   * Tracks a custom event in Segment and DataDog. If you're tracking a click, use .trackClick from this class.
   * @param eventName The name of the event. It should be kabab-case and end with 'clicked', 'viewed', 'submitted', 'filled'.
   * @param product One of the surfaces defined by the Product type.
   * @param productSurface The name of the of the product surface
   * @param value Keys and values to be saved with the event.
   * @returns Returns nothing.
   */
  track(eventName: string, product: Product, productSurface: ProductSurface, value: Record<string, unknown> = {}) {
    window.DD_RUM && window.DD_RUM.addAction(eventName, {...value, product, productSurface})
    if (!this.isDefined) return
    window.analytics.track(eventName, {...value, branch: process.env.BRANCH, product, productSurface})
  }

  /**
   * trackClick standardizes our click tracking by enforcing a click event syntax, adding a product, and adding productSurface.
   * @param target The target of the user's action, which includes a description and an html element that received the action. Example: learn-more-button
   * @param product One of the surfaces defined by the Product type.
   * @param productSurface One of the surfaces defined by the ProductSurface type.
   * @param properties Arbitrary properties that will help analysis later.
   * @returns Returns nothing
   */
  trackClick(
    target: string,
    product: Product,
    productSurface: ProductSurface,
    properties: Record<string, unknown> = {},
  ) {
    window.DD_RUM &&
      window.DD_RUM.addAction(`${target}-clicked`, {
        ...properties,
        product,
        productSurface,
      })
    if (!this.isDefined) return
    window.analytics.track(`${target}-clicked`, {
      ...properties,
      product,
      productSurface,
    })
  }
}

export default new Analytics()
